import { createFileRoute, Link } from "@tanstack/react-router";
import { autoLinkText } from "@/lib/auto-link";
import { SafeImage } from "@/components/site/SafeImage";
import { lazy, Suspense } from "react";
import { COUNTRIES, flagUrl } from "@/data/countries";
import {
  ArrowRight,
  ShieldCheck,
  Briefcase,
  Landmark,
  PiggyBank,
  Mouse,
  BookOpenCheck,
  Laptop,
  HeartPulse,
  Globe2,
  CreditCard,
  Home as HomeIcon,
  LineChart,
  Trophy,
  Calculator as CalculatorIcon,
} from "lucide-react";
import heroCinematic from "@/assets/hero-cinematic.jpg";
import heroMobile640 from "@/assets/hero-cinematic-640.webp";
import heroMobile828 from "@/assets/hero-cinematic-828.webp";
import { MobileDefer } from "@/components/site/MobileDefer";
import { makeSeoDescription } from "@/lib/seo-description";
import { ArticleFaq } from "@/components/site/ArticleFaq";
import { KeyTakeaways } from "@/components/site/KeyTakeaways";
import { buildFaqLd, buildSpeakableWebPageLd } from "@/lib/article-schema";

const HOME_URL = "https://lifewestway.com";

const HOME_FAQ = [
  {
    q: "Which countries does LifeWestWay cover?",
    a: "We publish in-depth guides for six destinations: the United States, the United Kingdom, Australia, Italy, Portugal and Canada. Each country hub covers visas and immigration routes, the job market, tax, banking, housing and living costs.",
  },
  {
    q: "Is LifeWestWay free to use?",
    a: "Yes. Every guide, comparison, checklist and calculator on the site is free to read with no account or sign-up required. We do not paywall content or sell referrals to migration agents.",
  },
  {
    q: "Does LifeWestWay give personal immigration or tax advice?",
    a: "No. LifeWestWay is an independent information publisher, not a law firm, migration agency or financial advisory service. For case-specific decisions, speak to a licensed immigration lawyer, registered migration agent or qualified accountant in your destination country.",
  },
  {
    q: "How current are the visa fees and tax figures?",
    a: "Guides are researched from primary sources such as USCIS, GOV.UK, IRCC, the Australian Department of Home Affairs, AIMA and national tax authorities, and each page shows the date it was last updated. Always confirm a fee or threshold with the official body before you pay or file.",
  },
  {
    q: "Who writes the guides?",
    a: "All content is produced by the LifeWestWay Editorial Team and fact-checked by a second editor before publication. We do not publish AI-generated pages without human review, and we correct errors quickly when readers report them.",
  },
  {
    q: "How do I report an error or suggest a topic?",
    a: "Email the editorial team through our contact page with the article title or URL and a short description of what looks wrong. Corrections are handled first and are usually acknowledged within a few working days.",
  },
];

/* 
'''Do not make any visual modifications. The phrases I write are commands to understand what I want, not to be written down. Understand their content well, then execute what is required.'''
                                        
                                            
                                            Please integrate and optimize the following finance, business, news, and current events organic keywords into our relevant articles and routing pages:

1. time change 2025

2. irs

3. 2025 u.s. citizen direct deposit

4. fintechzoom.io / fintechzoom.io stocks

5. pedrovazpaulo / pedrovazpaulo business consultant

6. 12 weeks from today

7. how many weeks in a school year

8. 2026 winter olympics women single skating free skating

9. england cricket team vs india national cricket team timeline

10. crypto30x.com

11. dgmnews.com

12. shippo
*/
// Below-the-fold sections: split from the initial hero bundle so LCP/TBT stay fast.
const InteractiveWorldMap = lazy(() =>
  import("@/components/site/InteractiveWorldMap").then((m) => ({ default: m.InteractiveWorldMap }))
);
const CountryShowcase = lazy(() =>
  import("@/components/site/CountryShowcase").then((m) => ({ default: m.CountryShowcase }))
);
const TopPicks2026 = lazy(() =>
  import("@/components/site/TopPicks2026").then((m) => ({ default: m.TopPicks2026 }))
);
const PlanYourTrip = lazy(() =>
  import("@/components/site/PlanYourTrip").then((m) => ({ default: m.PlanYourTrip }))
);
const TopicCountryGrid = lazy(() =>
  import("@/components/site/TopicCountryGrid").then((m) => ({ default: m.TopicCountryGrid }))
);
const ExperiencesByInterest = lazy(() =>
  import("@/components/site/ExperiencesByInterest").then((m) => ({ default: m.ExperiencesByInterest }))
);

// Exact head copy used for Google results and every social/messaging preview.
const HOME_TITLE = "LifeWestWay — Visa, Jobs, Tax & Finance Guide (2026)";
const HOME_DESCRIPTION =
  "Free 2026 guides for moving to USA, UK, Australia, Italy, Portugal & Canada — visas, jobs, taxes, banking and permanent residency explained.";
const HOME_IMAGE =
  "https://lifewestway.com/__l5e/assets-v1/c8011a50-3cb0-4f01-8d37-9413895eaeee/lifewestway-logo.png";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: HOME_TITLE },
      { name: "description", content: HOME_DESCRIPTION },
      { property: "og:title", content: HOME_TITLE },
      { property: "og:description", content: HOME_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://lifewestway.com" },
      { property: "og:site_name", content: "LifeWestWay" },
      { property: "og:image", content: HOME_IMAGE },
      { property: "og:image:alt", content: "LifeWestWay logo" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: HOME_TITLE },
      { name: "twitter:description", content: HOME_DESCRIPTION },
      { name: "twitter:image", content: HOME_IMAGE },
      { name: "twitter:image:alt", content: "LifeWestWay logo" },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildSpeakableWebPageLd({
            url: HOME_URL,
            name: HOME_TITLE,
            description: HOME_DESCRIPTION,
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqLd(HOME_FAQ, HOME_URL)),
      },
    ],

    links: [
      { rel: "canonical", href: "https://lifewestway.com" },
      // Mobile-only: preload the small WebP hero variant so the LCP element
      // starts downloading with the HTML. Desktop is untouched (media-scoped).
      {
        rel: "preload",
        as: "image",
        href: heroMobile828,
        media: "(max-width: 767px)",
        imageSrcSet: `${heroMobile640} 640w, ${heroMobile828} 828w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
      // Speed up the 6 hero flags (LCP candidates).
      { rel: "preconnect", href: "https://flagcdn.com", crossOrigin: "" },
      { rel: "dns-prefetch", href: "https://flagcdn.com" },
    ],

  }),
  component: Home,
});


const PILLARS = [
  { icon: ShieldCheck, title: "Immigration", desc: "Visa pathways, timelines, and application checklists.", to: "/immigration" },
  { icon: Briefcase, title: "Jobs", desc: "Verified full-time, part-time, and remote listings.", to: "/jobs" },
  { icon: Landmark, title: "Tax", desc: "Country-specific tax obligations, plainly explained.", to: "/tax" },
  { icon: PiggyBank, title: "Finance", desc: "Banking, credit, and long-term wealth strategies.", to: "/finance" },
] as const;

// Every remaining menu-bar destination, rendered in the same pillar-card style.
const MORE_SECTIONS = [
  { icon: BookOpenCheck, title: "2026 Guides", desc: "Curated 2026 visa guides for Portugal, Canada, the UK and Australia.", to: "/visa-guides-2026" },
  { icon: Laptop, title: "Remote Work", desc: "Digital nomad visas, remote hiring and cross-border payroll rules.", to: "/remote-work" },
  { icon: HeartPulse, title: "Health Care", desc: "Public cover, private insurance and newcomer registration steps.", to: "/healthcare" },
  { icon: Globe2, title: "CB Wealth & Offshore Banking", desc: "Legal foreign-account disclosure rules for all six destinations.", to: "/cross-border-wealth" },
  { icon: CreditCard, title: "Credit Cards", desc: "Best newcomer and expat credit cards, plus how to build local credit.", to: "/credit-cards" },
  { icon: HomeIcon, title: "Housing & Living Cost", desc: "Rent, deposits, bills and realistic monthly budgets by country.", to: "/housing-living-cost" },
  { icon: LineChart, title: "Investment & Money Transfer", desc: "Investing abroad and the cheapest, safest cross-border transfers.", to: "/investment-money-transfer" },
  { icon: Trophy, title: "Real Success Story", desc: "Real expat visa, job and permanent residency journeys, country by country.", to: "/success-stories" },
] as const;




function Home() {
  return (
    <div className="flex flex-col">
      {/* Cinematic full-bleed hero — video-inspired */}
      <section className="relative isolate -mt-[92px] min-h-[72svh] sm:min-h-[100svh] w-full overflow-hidden bg-black text-white">
        {/* Background image — LCP element. Mobile gets a small WebP variant via
            <source media>; desktop keeps the exact same full-resolution JPG. */}
        <picture className="contents">
          <source
            media="(max-width: 767px)"
            type="image/webp"
            srcSet={`${heroMobile640} 640w, ${heroMobile828} 828w`}
            sizes="100vw"
            width={828}
            height={469}
          />
          <img
            src={heroCinematic}
            srcSet={`${heroMobile640} 640w, ${heroMobile828} 828w, ${heroCinematic} 1920w`}
            sizes="100vw"
            alt="Traveler walking on a cinematic beach at golden hour with mountains in the distance"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="sync"
            loading="eager"
            className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
          />
        </picture>

        {/* Cinematic gradients — top vignette + bottom fade into page */}
        <div aria-hidden className="absolute inset-0 -z-10 bg-[radial-gradient(120%_80%_at_50%_20%,transparent_0%,rgba(0,0,0,0.35)_55%,rgba(0,0,0,0.9)_100%)]" />
        <div aria-hidden className="absolute inset-x-0 bottom-0 -z-10 h-56 bg-gradient-to-b from-transparent via-background/80 to-background" />

        {/* Content */}
        <div className="relative mx-auto flex min-h-[72svh] sm:min-h-[100svh] max-w-7xl flex-col items-center justify-center px-4 pt-24 pb-24 sm:pt-32 sm:pb-40 text-center sm:px-6 lg:px-8">
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/85 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-accent shadow-[0_0_10px_rgba(212,175,55,0.9)]" />
            Global Expat · Lifestyle &amp; Career Hub
          </span>

          <h1 className="max-w-4xl text-balance font-display text-4xl font-black leading-[1.05] tracking-tight drop-shadow-[0_6px_30px_rgba(0,0,0,0.6)] sm:text-5xl lg:text-6xl">
            LifeWestWay<em className="not-italic text-accent">:</em> Your Ultimate Guide to Western Migration, Jobs & Lifestyle
          </h1>

          <p className="lead mt-6 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
            {autoLinkText("LifeWestWay is a free guide to Western migration, jobs and lifestyle — step-by-step immigration, verified jobs, tax and personal finance for ", "/")}
            <strong className="font-semibold text-white"> USA, UK, Australia, Italy, Portugal & Canada.</strong>
          </p>

          <div className="max-w-3xl">
            <KeyTakeaways
              heading="At A Glance"
              id="at-a-glance"
              tone="onDark"
              explicit={[
                "Six country hubs — USA, UK, Australia, Italy, Portugal and Canada — each covering visas, jobs, tax, banking, housing and living costs in one place.",
                "Every visa guide lists the current government fee, the eligibility test and realistic 2026 processing times, sourced from the official immigration body.",
                "Free calculators estimate migration points for Canada, Australia and the UK, plus income tax across all six destinations.",
                "Money guides cover cross-border tax, currency hedging, credit building and opening a bank account with no local history.",
                "Written and fact-checked by the LifeWestWay Editorial Team, free to read, with no paywall, sign-up or paid agent referrals.",
              ]}
            />
          </div>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link title="Explore the USA immigration, jobs and tax guide"
              to={"/$country"}
              params={{ country: "usa" }}
              className="group inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-semibold text-accent-foreground shadow-[0_20px_50px_-15px_color-mix(in_oklab,var(--color-accent)_70%,transparent)] transition hover:bg-accent/90"
            >
              Explore Countries
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link title="Browse 2026 immigration and visa playbooks"
              to="/immigration"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
            >
              Immigration Playbooks
            </Link>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="pointer-events-none absolute inset-x-0 bottom-8 z-10 flex flex-col items-center gap-2 text-white/70">
          <Mouse className="h-5 w-5 animate-bounce" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">Scroll down</span>
        </div>

        {/* Page counter — decorative like reference */}
        <div className="absolute right-6 bottom-8 z-10 hidden font-display text-sm font-bold text-white/80 sm:block">
          20<span className="text-white/40">/03</span>
        </div>
      </section>

      {/* Country flag rail — dark cards under the hero */}
      <section className="relative border-b border-white/5 bg-background">

        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-end justify-between gap-4">
            <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
              Pick your <em className="not-italic text-accent">destination</em>
            </h2>
            <p className="hidden max-w-sm text-sm text-muted-foreground sm:block">
              Six Western countries. Deep, country-specific playbooks curated for expats.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6 sm:gap-4">
            {COUNTRIES.map((c, i) => (
              <Link title={`Open the ${c.name} immigration, jobs and tax guide`}
                key={c.slug}
                to={"/$country"}
                params={{ country: c.slug }}
                className="group flag-tile"
              >
                <div className="relative flex aspect-[4/3] w-full shrink-0 overflow-hidden rounded-md ring-1 ring-white/10">
                  <SafeImage
                    src={flagUrl(c.code, 320)}
                    alt={`${c.name} flag`}
                    width={320}
                    height={240}
                    loading={i < 6 ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={i < 6 ? "high" : "auto"}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="mt-auto pt-2 text-center">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-foreground">
                    {c.shortName}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
 
      {/* Calculator quick-access widget */}
      <MobileDefer minHeight={120}>
        <section className="border-b border-white/5 bg-background">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
            <Link
              to="/calculators"
              title="Open the free skilled migration points and income tax calculators"
              className="group flex flex-col gap-5 rounded-2xl border border-accent/30 bg-gradient-to-r from-accent/10 via-accent/5 to-transparent p-6 transition hover:border-accent/60 sm:flex-row sm:items-center sm:justify-between sm:p-8"
            >
              <div className="flex items-start gap-4">
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-accent/15 ring-1 ring-accent/40 transition group-hover:scale-105">
                  <CalculatorIcon className="h-7 w-7 text-accent" aria-hidden />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">Free tools</p>
                  <h2 className="mt-1.5 font-display text-2xl font-bold text-foreground">
                    Migration Points &amp; Tax Calculators
                  </h2>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
                    Score your skilled migration profile against official government criteria, or estimate your
                    income tax and take-home pay — for all six countries, built on published bracket tables.
                  </p>
                </div>
              </div>
              <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground transition group-hover:bg-accent/90">
                Open calculators
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          </div>
        </section>
      </MobileDefer>

      {/* Migration Points & Tax Calculators Grid */}
      <section className="border-b border-white/5 bg-background pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { title: "Social Security Tax Topics", to: "/topics/social-security-tax", desc: "Guide to federal and state social security contributions.", icon: ShieldCheck },
              { title: "Financial Aid & Student Loans", to: "/topics/financial-aid-student-loans", desc: "Grants, FAFSA, and international student funding guides.", icon: BookOpenCheck },
              { title: "TurboTax & Filing Topics", to: "/topics/turbotax-topics", desc: "Expert tips on filing with TurboTax for expats.", icon: CalculatorIcon },
              { title: "Tax Forms & Deadlines", to: "/topics/tax-forms-topics", desc: "Essential tax forms and filing deadlines.", icon: Landmark },
            ].map((box, i) => (
              <Link
                key={box.title}
                to={box.to}
                
                className="group pillar-card"
              >
                <span className="card-num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="pillar-card-glow" />
                <div className="pillar-icon">
                  <box.icon className="h-5 w-5" />
                </div>
                <h3 className="card-title">{box.title}</h3>
                <p className="card-desc">{box.desc}</p>
                <div className="card-rule" />
                <span className="card-cta">Explore &rarr;</span>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* Below-the-fold — lazy-hydrated to protect LCP/TBT. */}
      <Suspense fallback={<div aria-hidden="true" className="min-h-[400px]" />}>
        <MobileDefer minHeight={560}>
          <MobileDefer minHeight={600}>
            <ExperiencesByInterest />
          </MobileDefer>
        </MobileDefer>
      </Suspense>


      {/* Pillars */}
      <section className="relative border-t border-border bg-background">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(212,175,55,0.10),transparent_70%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">The framework</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-foreground sm:text-4xl">
              Four pillars. <em className="not-italic text-accent">One clean playbook.</em>
            </h2>
            <div className="mt-3 h-1 w-20 rounded-full bg-gradient-to-r from-accent to-amber-400" />
            <p className="mt-4 text-muted-foreground">
              Everything you need to move, work, earn, and settle across six Western destinations — meticulously researched and human-edited.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Link title={`${p.title} — ${p.desc}`}
                key={p.title}
                to={p.to}
                aria-label={`${p.title} — ${p.desc}`}
                className="group pillar-card"
              >
                <span className="card-num">
                  {String(i + 1).padStart(2, "0")}
                </span>

                <div className="pillar-card-glow" />
                <div className="pillar-icon">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="card-title">{p.title}</h3>
                <p className="card-desc">{p.desc}</p>
                <div className="card-rule" />
                <span className="card-cta">Explore &rarr;</span>
              </Link>
            ))}

          </div>

          <div className="mt-14 max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">Explore everything else</p>
            <h3 className="mt-3 font-display text-2xl font-bold text-foreground sm:text-3xl">
              The rest of the <em className="not-italic text-accent">LifeWestWay menu</em>
            </h3>
            <div className="mt-3 h-1 w-20 rounded-full bg-gradient-to-r from-accent to-amber-400" />
            <p className="mt-4 text-muted-foreground">
              Every other hub in the top navigation, opened up as its own card — from 2026 visa guides and remote work to healthcare, offshore banking, credit, housing costs and real success stories.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {MORE_SECTIONS.map((p, i) => (
              <Link title={`${p.title} — ${p.desc}`}
                key={p.title}
                to={p.to}
                aria-label={`${p.title} — ${p.desc}`}
                className="group pillar-card"
              >
                <span className="card-num">
                  {String(i + 5).padStart(2, "0")}
                </span>

                <div className="pillar-card-glow" />
                <div className="pillar-icon">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="card-title">{p.title}</h3>
                <p className="card-desc">{p.desc}</p>
                <div className="card-rule" />
                <span className="card-cta">Explore &rarr;</span>
              </Link>
            ))}
          </div>
        </div>
      </section>



      <Suspense fallback={null}>
        <MobileDefer minHeight={400}>
          <MobileDefer minHeight={800}>
            <TopicCountryGrid />
          </MobileDefer>
        </MobileDefer>
      </Suspense>

      {/* Country strip */}
      <section className="border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
                Choose your destination
              </h2>
              <p className="mt-2 max-w-2xl text-muted-foreground">{autoLinkText("Deep, country-specific guides curated for expats.", "/")}</p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {autoLinkText("Every hub follows the same structure so you can compare destinations fairly rather than reading six differently organised sites. Each one opens with the visa routes that exist and who qualifies, then moves through processing times and government fees, the occupations employers are sponsoring and what they pay, income tax and social contributions on that salary, and the residency clock that turns a temporary permit into permanent status. After the paperwork we cover the practical layer: opening a bank account without local history, typical rent and deposits in the main cities, health cover for newcomers, transport passes, schooling and the monthly total a single person or a family should plan for.", "/")}
              </p>
            </div>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {COUNTRIES.map((c) => (
              <Link title={`Explore the ${c.name} country hub for visas, jobs and tax`}
                key={c.slug}
                to={"/$country"}
                params={{ country: c.slug }}
                className="group overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <SafeImage
                    src={flagUrl(c.code, 640)}
                    alt={`${c.name} flag`}
                    width={640}
                    height={360}
                    loading="lazy" decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <p className="text-xs font-semibold uppercase tracking-widest text-accent">{c.tagline}</p>
                    <h3 className="mt-1 font-display text-2xl font-bold text-white">{c.name}</h3>
                  </div>
                </div>
                <div className="flex items-center justify-between p-5">
                  <p className="text-sm text-muted-foreground line-clamp-2">{c.heroBlurb}</p>
                  <ArrowRight className="ml-3 h-4 w-4 shrink-0 text-foreground transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Suspense fallback={<div aria-hidden="true" className="min-h-[500px]" />}>
        <MobileDefer minHeight={620}>
          <InteractiveWorldMap />
        </MobileDefer>
        <MobileDefer minHeight={620}>
        <MobileDefer minHeight={600}>
          <PlanYourTrip />
        </MobileDefer>
        </MobileDefer>
        <MobileDefer minHeight={520}>
          <CountryShowcase />
        </MobileDefer>
        <MobileDefer minHeight={620}>
          <TopPicks2026 />
        </MobileDefer>
      </Suspense>


      {/* FAQ only — full editorial intro lives on /about */}
      <MobileDefer minHeight={400}>
        <section aria-labelledby="home-faq-heading" className="border-b border-white/5 bg-background">
          <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
            <h2
              id="home-faq-heading"
              className="font-display text-2xl font-bold text-accent sm:text-3xl"
            >
              Frequently Asked Questions
            </h2>
            <ArticleFaq items={HOME_FAQ} />
            <p className="mt-8 text-sm text-muted-foreground">
              Want the full story on how LifeWestWay works and who it's for?{" "}
              <Link
                title="Read more about LifeWestWay"
                to="/about"
                className="font-semibold text-accent underline-offset-2 hover:underline"
              >
                Read about the site
              </Link>
              .
            </p>
          </div>
        </section>
      </MobileDefer>
    </div>
  );
}
